import React, { useState, useEffect } from "react";
import Title from "../../../../components/title/Title";
import { MainContainer } from "../../../styles/sharedStyles";
import Section from "../../../../components/section/Section";
import { tokens } from "../../../../../theme";
import {
  Grid,
  Typography,
  Box,
  useTheme,
  Paper,
  useMediaQuery,
  Button,
} from "@mui/material";
import apiService from "../../../../../ApiService";
import { API_ENDPOINTS } from "../../../../../ApiService";
import MuscleGroupListingPage from "./MuscleGroupListingPage";
import InfoSelectWithDialog from "../../../../components/tooltip/infoSelectWithDialog/InfoSelectWithDialog";
import InfoTextFieldWithDialog from "../../../../components/tooltip/infoTextFieldWithDialog/InfoTextFieldWithDialog";

const MuscleGroupCreationPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Check if it's a mobile screen

  // State for form inputs
  const [name, setName] = useState("");
  const [frenchName, setFrenchName] = useState("");
  const [description, setDescription] = useState("");
  const [bodyPart, setBodyPart] = useState(""); // "upper" or "lower"
  const [region, setRegion] = useState(""); // e.g., "chest", "shoulders", "quads", etc.
  const [action, setAction] = useState(""); // "push" or "pull"

  // State to store fetched muscle groups
  const [muscleGroups, setMuscleGroups] = useState([]);

  // Fetch existing muscle groups from the API
  const fetchMuscleGroups = async () => {
    try {
      const response = await apiService.get(API_ENDPOINTS.FITNESS_GET_MUSCLE);
      setMuscleGroups(response.data);
    } catch (error) {
      console.error("Error fetching muscle groups:", error);
    }
  };

  useEffect(() => {
    fetchMuscleGroups();
  }, []);

  // Handle form submission to create a new muscle group
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Create the payload. Adjust the payload structure as needed by your API.
      const payload = {
        name,
        frenchName,
        description,
        bodyPart,
        region,
        action,
      };
      await apiService.post(API_ENDPOINTS.FITNESS_ADD_MUSCLE, payload);

      // Clear form fields after successful creation
      setName("");
      setFrenchName("");
      setDescription("");
      setBodyPart("");
      setRegion("");
      setAction("");
      // Refresh muscle groups list
      fetchMuscleGroups();
    } catch (error) {
      console.error("Error creating muscle group:", error);
    }
  };

  // Dropdown options for Body Part and Action
  const bodyPartOptions = [
    { value: "Upper Body", label: "Upper Body" },
    { value: "Lower Body", label: "Lower Body" },
    { value: "Core", label: "Core" },
  ];

  const actionOptions = [
    { value: "Push", label: "Push" },
    { value: "Pull", label: "Pull" },
    { value: "Stabilize", label: "Stabilize" },
  ];

  return (
    <MainContainer>
      <Title title="Create Muscle Group" summary="Formulaire" />
      <Section>
        <Paper
          elevation={3}
          sx={{
            p: 1.5, // smaller padding
            mb: 2,
            backgroundColor: colors.primary[400],
          }}
        >
          <Typography variant="body1" color={colors.grey[100]} align="left">
            Please enter the details for the new muscle group.
          </Typography>
        </Paper>
      </Section>

      <Box
        sx={{
          my: isMobile ? 1 : 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Grid container justifyContent="center">
          <Grid
            item
            xs={12}
            sm={10}
            md={8}
            lg={6}
            sx={{ p: isMobile ? 1 : 1.5 }}
          >
            <Box sx={{ p: 1 }}>
              {/* Form Section */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <form onSubmit={handleSubmit}>
                  <InfoTextFieldWithDialog
                    label="Muscle Group Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    infoTitle="Muscle Group Name"
                    infoDescription="Name: The muscle group name."
                    autoComplete="off"
                  />
                  <InfoTextFieldWithDialog
                    label="French Name"
                    value={frenchName}
                    onChange={(e) => setFrenchName(e.target.value)}
                    infoTitle="French Name"
                    infoDescription="French Name: The muscle group name in french."
                    autoComplete="off"
                  />
                  <InfoTextFieldWithDialog
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    infoTitle="Description"
                    infoDescription="Description: A brief description."
                    autoComplete="off"
                  />
                  <InfoSelectWithDialog
                    label="Body Part"
                    value={bodyPart}
                    onChange={(e) => setBodyPart(e.target.value)}
                    infoTitle="Body Part"
                    infoDescription='Body Part: A high-level categorization ("Upper Body," "Lower Body," or "Core").'
                    options={bodyPartOptions}
                  />
                  <InfoTextFieldWithDialog
                    label="Region (e.g., Chest, Shoulders, Quadriceps)"
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    infoTitle="Region"
                    infoDescription="Region: A sub-division within the body part. For the upper body: Chest, Shoulders, Traps, Back, or Arms; for the lower body: Quadriceps, Hamstrings, Glutes, or Calves; for core: Abs or Back."
                    autoComplete="off"
                  />
                  <InfoSelectWithDialog
                    label="Action"
                    value={action}
                    onChange={(e) => setAction(e.target.value)}
                    infoTitle="Action"
                    infoDescription='Action: A functional classification (for instance, whether the muscle is primarily used for "Push," "Pull," or "Stabilize").'
                    options={actionOptions}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    Create Muscle Group
                  </Button>
                </form>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Box>
      {/* Table Section */}
      <MuscleGroupListingPage />
    </MainContainer>
  );
};

export default MuscleGroupCreationPage;
